import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import qrcode from 'qrcode';
import { Box, Button, DialogActions, DialogContent, DialogTitle, OutlinedInput, Typography } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import { Alert } from '@material-ui/lab';
import feathersServices from '../../../services/feathersServices';

const UserManagementMFA = props => {
  const {
    open,
    onClose,
    createKey,
    validate,
    user,
    onSave,
    successMessage = "MFA setup completed successfully! You'll need to use your authenticator app for future logins. Please save your changes."
  } = props;

  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    generateSecret();
  }, []);

  const generateSecret = async () => {
    setIsLoading(true);
    setError('');
    try {
      const { value: key } = await createKey({
        user: user.email
      });

      // Generate QR code
      qrcode.toDataURL(key.url, (err, url) => {
        setQrCode(url);
      });

      setSecret(key.secret);
    } catch (err) {
      setError('Failed to generate MFA secret. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const verifyCode = async () => {
    setIsLoading(true);
    setError('');
    try {
      const { value: verified } = await validate(verificationCode.replace(/\s/g, ''), {
        query: {
          secret
        }
      });

      if (!verified) throw new Error('Invalid verification code');

      onSave(secret);
      setSuccess(true);
    } catch (err) {
      setError('Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogTitle>Set up MFA</DialogTitle>

      <DialogContent>
        {success ? (
          <Alert>{successMessage}</Alert>
        ) : (
          <Box mb={1}>
            <Typography>1. Install an authenticator app like Google Authenticator</Typography>
            <Typography>2. Scan this QR code or enter the secret key manually</Typography>

            {qrCode && (
              <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" m={1}>
                <Box display="flex" justifyContent="center" alignItems="center" p={2} border="1px solid" borderColor="grey.300" borderRadius={1}>
                  <img src={qrCode} alt="QR Code" />
                </Box>

                <Box display="flex" alignItems="center" mt={1}>
                  <Typography variant="body2">Secret Key:&nbsp;</Typography>
                  <code>{secret}</code>
                  <Button variant="text" onClick={copySecret} color="secondary">
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </Box>
              </Box>
            )}

            {qrCode && (
              <Box>
                <Typography>3. Enter the verification code from your authenticator app</Typography>

                <OutlinedInput
                  fullWidth
                  color="secondary"
                  placeholder="Enter 6-digit code"
                  value={verificationCode}
                  onChange={e => setVerificationCode(e.target.value)}
                  maxLength={6}
                />
              </Box>
            )}
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        {onClose && (
          <Button variant="text" onClick={onClose}>
            {success ? 'Close' : 'Cancel'}
          </Button>
        )}
        {!success && (
          <Button variant="contained" onClick={verifyCode} disabled={isLoading || verificationCode.length !== 6} color="secondary" disableElevation>
            Verify Code
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = state => {
  return {};
};

const mapDispatchToProps = {
  createKey: feathersServices.totp.create,
  validate: feathersServices.totp.get
};

export default connect(mapStateToProps, mapDispatchToProps)(UserManagementMFA);
